import React from "react"
import { twMerge } from "tailwind-merge"

import AnimatedSection from "../../ProProfileShared/AnimatedSection"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../constants"
import { getHeroImageSrcSet } from "../utils/heroImageUtils"
import {
  getReviewHeroLayout,
  getReviewHeroOverlayOpacity,
  getReviewHeroPrimaryImageUrl,
  getReviewHeroSectionBackgroundColor,
  getReviewHeroTextColor,
  reviewHeroHeaderSyncStyle
} from "../utils/reviewHeroLayoutShared"
import { getReviewHeroImage } from "../utils/reviewHeroUtils"
import { useOptionalWebsiteBuilderV2 } from "../WebsiteBuilderV2Context"

import ReviewHeroContent from "./ReviewHeroContent"
import ShapeBorder, { getBaseLayoutFromShapeLayout, getShapeTypeFromLayout, hasShapeBorder } from "./ShapeBorder"

const ReviewHeroImagePanel = ({ imageUrl, className, style }) =>
  imageUrl ? (
    <div className={className} style={{ ...style, backgroundImage: `url('${imageUrl}')` }} />
  ) : (
    <div className={twMerge(className, "flex items-center justify-center bg-gray-200")} style={style}>
      <span className="text-gray-500">No image selected</span>
    </div>
  )

const ReviewHeroSectionDesktop = ({ section, practice, theme, highlightedField }) => {
  const builderContext = useOptionalWebsiteBuilderV2()
  const pages =
    builderContext?.pages?.length > 0 || builderContext?.publishedPages?.length > 0
      ? builderContext?.pages || builderContext?.publishedPages || []
      : practice?.pages || []

  const settings = section?.settings || {}
  const layout = getReviewHeroLayout(section)
  const effectiveLayout = getBaseLayoutFromShapeLayout(layout)
  const shapeType =
    settings.shapeBorder && settings.shapeBorder !== "none" ? settings.shapeBorder : getShapeTypeFromLayout(layout)
  const showShapeBorder = (settings.shapeBorder && settings.shapeBorder !== "none") || hasShapeBorder(layout)
  const isHighlighted = highlightedField === `section-${section.id}`
  const overlayOpacity = getReviewHeroOverlayOpacity(settings)
  const primaryImageUrl = getReviewHeroPrimaryImageUrl(section)
  const sectionBackgroundColor = getReviewHeroSectionBackgroundColor(section, theme, { useHeroImage: false })
  const images = settings.images || []

  const renderReviewContent = ({
    hasBackgroundImage = false,
    contentLayout = layout,
    isInWhiteContainer = false,
    align = "center",
    useHeroImage = false
  } = {}) => (
    <ReviewHeroContent
      section={section}
      practice={practice}
      theme={theme}
      align={align}
      hasBackgroundImage={hasBackgroundImage}
      textColor={getReviewHeroTextColor({
        layout: contentLayout,
        hasBackgroundImage,
        theme,
        isInWhiteContainer,
        settings,
        useHeroImage
      })}
    />
  )

  switch (effectiveLayout) {
    case "image_left":
      return (
        <section
          className={twMerge(
            "relative flex items-center justify-start",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor: sectionBackgroundColor }}>
          <style>{reviewHeroHeaderSyncStyle(sectionBackgroundColor)}</style>
          <AnimatedSection className="mx-auto w-full px-[140px]">
            <div className="flex min-h-[500px] items-center">
              <ReviewHeroImagePanel
                imageUrl={primaryImageUrl}
                className="h-[600px] min-h-[600px] w-1/2 bg-cover bg-center bg-no-repeat"
                style={{ margin: "1em 0" }}
              />
              <div className="flex w-1/2 flex-col items-start justify-center gap-[48px] px-[52px] py-8">
                {renderReviewContent({ align: "start" })}
              </div>
            </div>
          </AnimatedSection>
        </section>
      )

    case "image_right":
      return (
        <section
          className={twMerge(
            "relative flex items-center justify-start",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor: sectionBackgroundColor }}>
          <style>{reviewHeroHeaderSyncStyle(sectionBackgroundColor)}</style>
          <AnimatedSection className="mx-auto w-full px-[140px]">
            <div className="flex min-h-[600px] items-center">
              <div className="flex w-1/2 flex-col items-start justify-center gap-[48px] px-[52px] py-8">
                {renderReviewContent({ align: "start" })}
              </div>
              <ReviewHeroImagePanel
                imageUrl={primaryImageUrl}
                className="h-[600px] min-h-[600px] w-1/2 bg-cover bg-center bg-no-repeat"
                style={{ margin: "1em 0" }}
              />
            </div>
          </AnimatedSection>
        </section>
      )

    case "half_image_left":
      return (
        <section
          className={twMerge(
            "relative flex min-h-[728px] items-center",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor: sectionBackgroundColor }}>
          <style>{reviewHeroHeaderSyncStyle(sectionBackgroundColor)}</style>
          <AnimatedSection className="mx-auto w-full">
            <div className="flex min-h-[728px] items-center">
              <ReviewHeroImagePanel
                imageUrl={primaryImageUrl}
                className="h-[728px] min-h-[728px] w-1/2 bg-cover bg-center bg-no-repeat"
              />
              <div className="flex min-h-[728px] w-1/2 shrink-0 flex-col items-center justify-center bg-white px-[80px] py-[137px]">
                {renderReviewContent({ isInWhiteContainer: true })}
              </div>
            </div>
          </AnimatedSection>
        </section>
      )

    case "half_image_right":
      return (
        <section
          className={twMerge(
            "relative flex min-h-[728px] items-center",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor: sectionBackgroundColor }}>
          <style>{reviewHeroHeaderSyncStyle(sectionBackgroundColor)}</style>
          <AnimatedSection className="mx-auto w-full">
            <div className="flex min-h-[728px] items-center">
              <div className="flex min-h-[728px] w-1/2 shrink-0 flex-col items-center justify-center bg-white px-[80px] py-[137px]">
                {renderReviewContent({ isInWhiteContainer: true })}
              </div>
              <ReviewHeroImagePanel
                imageUrl={primaryImageUrl}
                className="h-[728px] min-h-[728px] w-1/2 shrink-0 bg-cover bg-center bg-no-repeat"
              />
            </div>
          </AnimatedSection>
        </section>
      )

    case "scattered_images": {
      const imageCount = images.length

      if (imageCount === 1) {
        return (
          <section
            className={twMerge(
              "relative flex items-center justify-center py-16",
              isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
            )}
            style={{ backgroundColor: sectionBackgroundColor, minHeight: "400px" }}>
            {images[0]?.url && (
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${images[0].url})` }}
              />
            )}
            <div className="absolute inset-0" style={{ backgroundColor: `rgba(0, 0, 0, ${overlayOpacity / 100})` }} />
            <AnimatedSection className="relative z-10 mx-auto max-w-4xl px-4 py-8">
              {renderReviewContent({ hasBackgroundImage: true, contentLayout: "full_image" })}
            </AnimatedSection>
          </section>
        )
      }

      if (imageCount === 2) {
        return (
          <section
            className={twMerge(
              "relative flex items-center justify-center",
              isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
            )}
            style={{ backgroundColor: sectionBackgroundColor, minHeight: "400px" }}>
            <style>{reviewHeroHeaderSyncStyle(sectionBackgroundColor)}</style>
            <div className="relative mx-auto w-full max-w-splash-container px-0">
              <div
                className="absolute h-300 w-[280px] rounded-lg bg-cover bg-center"
                style={{ left: "80px", top: "50px", backgroundImage: `url(${images[0]?.url})` }}
              />
              <div
                className="absolute right-[60px] top-1/2 h-[280px] w-[320px] -translate-y-1/2 rounded-lg bg-cover bg-center"
                style={{ backgroundImage: `url(${images[1]?.url})` }}
              />
              <div
                className="relative left-1/2 top-1/2 z-10 w-full -translate-x-1/2 -translate-y-1/2 bg-white px-12 py-10 text-center"
                style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
                <AnimatedSection>{renderReviewContent({ isInWhiteContainer: true })}</AnimatedSection>
              </div>
            </div>
          </section>
        )
      }

      return (
        <section
          className={twMerge(
            "relative flex items-center justify-center",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor: sectionBackgroundColor, minHeight: "500px" }}>
          <style>{reviewHeroHeaderSyncStyle(sectionBackgroundColor)}</style>
          <div className="relative mx-auto w-full max-w-[1440px] px-0" style={{ minHeight: "600px" }}>
            <div
              className="absolute left-1/2 top-1/2 z-20 w-[517px] -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white px-8 py-12 text-center"
              style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.15)" }}>
              <AnimatedSection>{renderReviewContent({ isInWhiteContainer: true })}</AnimatedSection>
            </div>
            {images.length > 0 && (
              <div
                className="absolute h-[216px] w-[279px] rounded-lg bg-cover bg-center shadow-lg"
                style={{ left: "136px", top: "63px", backgroundImage: `url(${images[0]?.url})` }}
              />
            )}
            {images.length > 1 && (
              <div
                className="absolute h-[326px] w-[422px] rounded-lg bg-cover bg-center shadow-lg"
                style={{ left: "1041px", top: "75px", backgroundImage: `url(${images[1]?.url})` }}
              />
            )}
            {images.length > 2 && (
              <div
                className="absolute h-[384px] w-[496px] rounded-lg bg-cover bg-center shadow-lg"
                style={{
                  left: "-197px",
                  top: "206px",
                  transform: "rotate(180deg) scaleY(-1)",
                  backgroundImage: `url(${images[2]?.url})`
                }}
              />
            )}
          </div>
        </section>
      )
    }

    case "overlap_double_image": {
      if (images.length >= 2) {
        return (
          <section
            className={twMerge(
              "relative flex items-center justify-center",
              isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
            )}
            style={{ backgroundColor: sectionBackgroundColor, minHeight: "400px" }}>
            <style>{reviewHeroHeaderSyncStyle(sectionBackgroundColor)}</style>
            <div className="relative mx-auto w-full max-w-splash-container px-0">
              <div className="relative flex h-full items-center justify-center">
                <div
                  className="absolute h-[385px] w-[385px] rounded-lg bg-cover bg-center shadow-lg"
                  style={{
                    left: "0px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    backgroundImage: `url(${images[0]?.url})`,
                    zIndex: 1
                  }}
                />
                <div
                  className="relative z-20 flex w-[626px] items-center justify-center bg-white p-14 text-center shadow-lg"
                  style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.15)" }}>
                  <AnimatedSection>{renderReviewContent({ isInWhiteContainer: true })}</AnimatedSection>
                </div>
                <div
                  className="absolute h-[385px] w-[385px] rounded-lg bg-cover bg-center shadow-lg"
                  style={{
                    right: "0px",
                    top: "50%",
                    transform: "translateY(-50%) rotate(180deg) scaleY(-1)",
                    backgroundImage: `url(${images[1]?.url})`,
                    zIndex: 1
                  }}
                />
              </div>
            </div>
          </section>
        )
      }

      if (images.length === 1) {
        return (
          <section
            className={twMerge(
              "relative flex items-center justify-center py-16",
              isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
            )}
            style={{ backgroundColor: theme?.colors?.primary || "#3B82F6", minHeight: "400px" }}>
            <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${images[0].url})` }} />
            <div className="absolute inset-0" style={{ backgroundColor: `rgba(0, 0, 0, ${overlayOpacity / 100})` }} />
            <AnimatedSection className="relative z-10 mx-auto max-w-4xl px-4">
              {renderReviewContent({ hasBackgroundImage: true, contentLayout: "full_image" })}
            </AnimatedSection>
          </section>
        )
      }

      return (
        <section
          className={twMerge(
            "relative flex items-center justify-center",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor: theme?.colors?.bgLight || "#FFFFFF", minHeight: "300px" }}>
          <style>{reviewHeroHeaderSyncStyle(theme?.colors?.bgLight || "#FFFFFF")}</style>
          <AnimatedSection className="mx-auto max-w-4xl px-4 py-8">
            {renderReviewContent({ isInWhiteContainer: true })}
          </AnimatedSection>
        </section>
      )
    }

    case "contained_image":
      return (
        <section
          className={twMerge(
            "relative flex items-center justify-center p-8",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor: theme?.colors?.bgLight || "#FFFFFF", minHeight: "400px" }}>
          <div className="absolute inset-8 overflow-hidden rounded-lg">
            {primaryImageUrl && (
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${primaryImageUrl})` }}
              />
            )}
            <div className="absolute inset-0" style={{ backgroundColor: `rgba(0, 0, 0, ${overlayOpacity / 100})` }} />
          </div>
          <AnimatedSection className="relative z-10 mx-auto max-w-4xl px-4 py-8">
            {renderReviewContent({ hasBackgroundImage: true, contentLayout: "contained_image" })}
          </AnimatedSection>
        </section>
      )

    case "full_image":
    default: {
      const { heroImageUrl, useHeroImage } = getReviewHeroImage({ section, practice, pages })
      const backgroundColor = useHeroImage
        ? "transparent"
        : getReviewHeroSectionBackgroundColor(section, theme, { useHeroImage: false })
      const fullImageMinHeight = shapeType === "point_up_opacity" ? "700px" : "520px"

      return (
        <section
          className={twMerge(
            "relative flex items-center justify-center",
            useHeroImage ? "py-20" : "py-16 md:py-20",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor, minHeight: useHeroImage ? fullImageMinHeight : undefined }}>
          {!useHeroImage && <style>{reviewHeroHeaderSyncStyle(backgroundColor)}</style>}
          {useHeroImage && heroImageUrl && (
            <>
              <img
                src={heroImageUrl}
                srcSet={getHeroImageSrcSet(heroImageUrl) || undefined}
                alt=""
                className="absolute inset-0 size-full object-cover object-center"
                loading="eager"
                fetchpriority="high"
              />
              <div className="absolute inset-0" style={{ backgroundColor: `rgba(0, 0, 0, ${overlayOpacity / 100})` }} />
            </>
          )}
          <div className="relative z-10 mx-auto max-w-5xl px-6 text-center">
            {renderReviewContent({
              hasBackgroundImage: useHeroImage,
              contentLayout: "full_image",
              useHeroImage
            })}
          </div>
          {useHeroImage && showShapeBorder && shapeType && (
            <div className="absolute inset-x-0 bottom-0 z-20">
              <ShapeBorder type={shapeType} color={theme?.colors?.bgLight || "#FFFFFF"} isMobile={false} />
            </div>
          )}
        </section>
      )
    }
  }
}

export default ReviewHeroSectionDesktop
